import { BCMSDate } from "@bcm-dev/bcms-vite";
import { Biller, Payment } from "model";
import React from "react";

/**
 * @class ActivityFilter
 * @classdesc Class definition for the activity filter class
 */
export default class ActivityFilter {
    _state;
    _setState;

    constructor(state, setState) {
        this._state = state;
        this._setState = setState;
    }

    /**
     * @function state
     * @instance
     * @param {React.state} value - The value to be set for the state property
     * @description Setter for the state property
     * @memberof ActivityFilter
     */
    set state(value) { this._state = value; }
    /**
     * @function setState
     * @instance
     * @param {React.state} value - The value to be set for the setState property
     * @description Setter for the setState property
     * @memberof ActivityFilter
     */
    set setState(value) { this._setState = value; }

    /**
     * @function state
     * @instance
     * @returns {React.state}
     * @description Getter for the state property
     * @memberof ActivityFilter
     */
    get state() { return this._state; }
    /**
     * @function setState
     * @instance
     * @returns {React.state}
     * @description Getter for the setState property
     * @memberof ActivityFilter
     */
    get setState() { return this._setState; }

    /**
     * @function filters
     * @instance
     * @returns {OBJECT}
     * @description Getter for the filters within the state
     * @memberof ActivityFilter
     */
    get filters() { return this._state.filters; }
    /**
     * @function filtered
     * @instance
     * @returns {ARRAY}
     * @description Getter for the filtered array within state
     * @memberof ActivityFilter
     */
    get filtered() { return this._state.filtered; }

    /**
     * @function resetAll
     * @instance
     * @returns {VOID}
     * @description Update the state to have the default filters and filtered array
     * @memberof ActivityFilter
     */
    resetAll() {
        const { filters, filtered } = ActivityFilter.defaults();
        this.setState({ filters, filtered });
    }

    /**
     * @function reset
     * @param {ARRAY} values - The properties that should be reset in the filters
     * @param {BOOLEAN} resetToDefault - If true, resets all the filter properties as they existed by default.
     * @instance
     * @returns {VOID}
     * @description Resets the state filters based on the properties that are passed to the function
     * @memberof ActivityFilter
     */
    reset(properties, resetToDefault) {
        const { filters:defaultFilters } = ActivityFilter.defaults();
        const filters = Object.keys(this.filters).reduce((result, key) => {
            if(!properties.includes(key)) { result[key] = this.filters[key]; }
            else if(defaultFilters[key] !== undefined) { result[key] = defaultFilters[key]; }
            
            return result;
        }, {});

        if(resetToDefault) {
            this.setState(ActivityFilter.defaults());
        } else {
            this.update(filters);
        }
    }

    /**
     * @function setFilter
     * @instance
     * @param {STRING} prop - The property name of that should be set
     * @param {*} value - The value that should be set for the property
     * @returns {VOID}
     * @description Sets the filter property to the state
     * @memberof ActivityFilter
     */
    setFilter(prop, value) {
        const filters = { ...this.state.filters, [prop] : value };
        this.update(filters);
    }

    /**
     * @function update
     * @instance
     * @param {OBJECT} filters - The filters that should be used to filter the filtered array
     * @returns {VOID}
     * @description Function to update the filtered array based on the filters set in state
     * @memberof ActivityFilter
     */
    update(filters) {
        let filteredPayments = Object.keys(filters).reduce((result, key) => {
            switch(key) {
                case "date":
                    const offsetDate = BCMSDate.init().offset(filters[key].value);
                    return result.filter(payment => payment.date.isAfter(offsetDate), []);
                case "sort":
                    const sort = filters[key] ? filters[key].toLowerCase() : undefined;
                    if(sort === undefined) return result;
                    return ActivityFilter.sortPaymentsByType(sort, result);
                    
                case "status":
                    const status = filters[key].toLowerCase();
                    return status !== "all"
                        ? result.filter(payment => payment.status.toLowerCase() === status, [])
                        : result;

                case "search":
                    const search = filters[key];
                    return search instanceof Biller
                        ? result.filter(payment => payment.to instanceof Biller && payment.to.id === search.id, [])
                        : result;

                case "custom":
                    const { startDate, endDate } = filters[key].value;
                    return result.filter(payment => payment.date.isAfter(startDate) && payment.date.isBefore(endDate), []);
                
                default:
                    return result;
            }
        }, Payment.sortDateAscending(Payment.initAll()));

        this.setState({ filters, "filtered": filteredPayments });
    }

    // Sorts payments by type like "Date - Ascending" or "Name - (A-Z)".
    static sortPaymentsByType(type, payments) {
        return payments.sort((a, b) => {
            if(type.includes("date - ascending")) {
                return a.date.date.getTime() - b.date.date.getTime();
            } else if(type.includes("date - descending")) {
                return b.date.date.getTime() - a.date.date.getTime();
            } else if(type.includes("name - (a-z)")) {
                return a.to.name.localeCompare(b.to.name);
            } else if(type.includes("name - (z-a)")) {
                return b.to.name.localeCompare(a.to.name);
            } else {
                return 0;
            }
        });
    }

    /**
     * @function sortScheduledPayments
     * @static
     * @param {ARRAY} payments - All payments
     * @returns {ARRAY}
     * @description Returns all payments with the "scheduled" sorted ascending (ex. June 1 -> June 2 -> June 3)
     * since there are specific conditions where that is true. This type of sorting is not needed
     * when "Sort" is applied by the user or when only "All" is applied by the user.
     * @memberof ActivityFilter
     */
    static sortScheduledPaymentsByDateAscending(payments) {
        let scheduled = payments.filter((payment) => {
            return payment.status === "scheduled";
        });
        payments = payments.filter((payment) => {
            return payment.status !== "scheduled";
        });

        scheduled = ActivityFilter.sortPaymentsByType("date - ascending", scheduled);
        return [ ...payments, ...scheduled ];
    }

     /**
     * @function getCustomDefaultDates
     * @param {STRING} prop - A date range key like "-30" or "custom" 
     * in order to get a value like "30 days" or "Custom Date Range".
     * @static
     * @returns {STRING} - Returns a value like "30 days" or "Custom Date Range".
     * @description A date range key like "-30" or "custom" is used
     * in order to get a value like "30 days" or "Custom Date Range".
     * @memberof ActivityFilter
     */
    static getDateRangeLabel(prop) {
        const obj = {
            "-180": "Last 6 Months",
            "-30": "30 days",
            "-60": "60 days",
            "-90": "90 days",
            "-120": "120 days",
            "-365": "1 year",
            "-730": "2 years",
            "custom": "Custom date range"
        };
        return obj[prop];
    }

    /**
     * @function getCustomDefaultDates
     * @param {VOID}
     * @static
     * @returns {OBJECT} - Returns the default date ranges for the custom date range.
     * @description Returns the default date ranges for the custom date range.
     * @memberof ActivityFilter
     */
    static getCustomDefaultDates() {
        // Start: The start date is the first day of the current month. 
        // The end date is the last day of the current month.
        const startDate = new Date();
        startDate.setDate(1);

        const endDate = new Date();
        endDate.setMonth(endDate.getMonth() + 1, 1);
        endDate.setDate(endDate.getDate() - 1);
        return { startDate: BCMSDate.init(startDate), endDate: BCMSDate.init(endDate) };
        // END: The start date is the first day of the current month. 
        // The end date is the last day of the current month.
    }

    /**
     * @function init
     * @static
     * @param {React.state} state - The state instance from the useState hook
     * @param {React.state} setState - The setState function from the useState hook
     * @returns {ActivityFilter}
     * @description Returns a new instance of ActivityFilter
     * @memberof ActivityFilter
     */
    static init(state, setState) {
        return new ActivityFilter(state, setState);
    }

    /**
     * @function defaults
     * @static
     * @returns {OBJECT}
     * @description Returns an object of the default filters and filtered array for state
     * @memberof ActivityFilter
     */
    static defaults() {
        let allPayments = Payment.sortDateAscending(Payment.initAll());
        
        // "Scheduled" payments are ordered ascending only by default
        // or if the user specifically sets that sort.
        allPayments = ActivityFilter.sortScheduledPaymentsByDateAscending(allPayments);

        // "didFilter" is used to keep track if the user began to filter and clicked the "Apply" button
        // since the ui for the payments changes before and after filtering the payments.
        return { "filters": { "date": { value: "-180", label: "Last 6 Months" }, "status": "All", "sort": "None", "didFilter": false }, "filtered": allPayments };
    }
}
